import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase.js';
import { today, formatTime12h, friendlyToday } from '../../lib/dates.js';

export default function SummaryCard({ briefing, onCheckOut }) {
  const [checkoutDone, setCheckoutDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    const date = today();
    async function load() {
      const { data } = await supabase
        .from('daily_checkout')
        .select('completed_at')
        .eq('date', date)
        .maybeSingle();
      if (mounted) setCheckoutDone(!!data?.completed_at);
    }
    load();

    const channel = supabase
      .channel(`summary_checkout:${date}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'daily_checkout',
          filter: `date=eq.${date}`,
        },
        (payload) => setCheckoutDone(!!payload.new?.completed_at)
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  // Brighten the check-out button after 5pm local time.
  const evening = new Date().getHours() >= 17;

  return (
    <div className="max-w-md mx-auto bg-bg-card border border-border-base rounded p-6 animate-fade-in">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-text-secondary">
            Today's commitments
          </div>
          <h2 className="text-base font-semibold text-text-primary mt-1">
            {friendlyToday()}
          </h2>
        </div>
        <span className="text-xs px-2 py-1 bg-accent-green/15 text-accent-green rounded">
          ✓ Briefed
        </span>
      </div>

      <div className="space-y-2 text-sm">
        <Row label="Devotional" value={booleanLabel(briefing.devotional)} />
        <Row label="Workout" value={booleanLabel(briefing.workout)} />
        <Row label="Bed last night" value={formatTime12h(briefing.bed_time)} />
      </div>

      <div className="mt-4 border-t border-border-base pt-4">
        <div className="text-xs uppercase tracking-wider text-text-secondary mb-2">
          MITs
        </div>
        <ul className="space-y-1.5">
          {[1, 2, 3].map((i) => (
            <li key={i} className="flex items-start gap-2 text-text-primary">
              <span className="text-text-muted mt-0.5">{i}.</span>
              <span className="flex-1">{briefing[`mit_${i}_text`]}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Check-out CTA */}
      <div className="mt-5 border-t border-border-base pt-4">
        {checkoutDone ? (
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">Day wrapped</span>
            <span className="text-xs px-2 py-1 bg-accent-green/15 text-accent-green rounded">
              ✓ Checked out
            </span>
          </div>
        ) : (
          <button
            onClick={onCheckOut}
            className={`w-full py-2.5 rounded text-sm font-medium transition border ${
              evening
                ? 'bg-accent-blue text-white border-accent-blue hover:opacity-90'
                : 'bg-bg-input text-text-primary border-border-base hover:border-accent-blue/40'
            }`}
          >
            {evening ? 'Check out for the day →' : 'Check out for the day'}
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-secondary">{label}</span>
      <span className="text-text-primary">{value}</span>
    </div>
  );
}

function booleanLabel(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '—';
}
